import { ChevronDownIcon, ClockIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useRef, useState } from "react";
import { useClientTranslations } from "../../../../lib/i18n/client";
import { FormField } from "./FormField";
import styles from "./TimePicker.module.css";

interface TimePickerProps {
	id: string;
	label: string;
	required?: boolean;
	error?: string;
	placeholder?: string;
	/** "HH:mm" 24-hour internal representation, e.g. "14:30". */
	value?: string;
	onChange: (v: string) => void;
}

const MINUTE_STEP = 5;
const MINUTES_PER_HOUR = 60;
const MINUTE_OPTIONS = Array.from(
	{ length: MINUTES_PER_HOUR / MINUTE_STEP },
	(_, i) => i * MINUTE_STEP,
);
const HOURS_24 = Array.from({ length: 24 }, (_, i) => i);
const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12

const padTwo = (n: number): string => String(n).padStart(2, "0");
const formatHHMM = (h: number, m: number): string =>
	`${padTwo(h)}:${padTwo(m)}`;

function parseHHMM(v: string | undefined): { hh: number; mm: number } | null {
	if (!v) return null;
	const match = /^(\d{2}):(\d{2})$/.exec(v);
	if (!match) return null;
	const hh = Number(match[1]);
	const mm = Number(match[2]);
	if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
	if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
	return { hh, mm };
}

function nextRoundHalfHour(now: Date = new Date()): { hh: number; mm: number } {
	const m = now.getMinutes();
	const next = m < 30 ? 30 : 60;
	let hh = now.getHours();
	const mm = next === 60 ? 0 : 30;
	if (next === 60) hh = (hh + 1) % 24;
	return { hh, mm };
}

/** Round arbitrary minutes to the nearest 5-minute increment shown in the column. */
function snapMinuteToStep(mm: number): number {
	return Math.round(mm / MINUTE_STEP) * MINUTE_STEP;
}

/** Convert internal 24h hour into 12-hour display with am/pm flag. */
function to12(hh24: number): { hour12: number; isPm: boolean } {
	const isPm = hh24 >= 12;
	const hour12 = hh24 % 12 === 0 ? 12 : hh24 % 12;
	return { hour12, isPm };
}

/** Convert 12-hour selection back into the internal 0..23 hour. */
function from12(hour12: number, isPm: boolean): number {
	if (hour12 === 12) return isPm ? 12 : 0;
	return isPm ? hour12 + 12 : hour12;
}

/**
 * Phase 37 — Custom 2-column hours/minutes time picker (D-11, D-12).
 *
 * Why custom (no native time input, no Intl-based formatting):
 * Capacitor iOS WKWebView (especially under Hebrew locale) silently fails for
 * native time inputs and locale time-string formatters (RESEARCH Pitfall 8 / 16).
 * This component renders 5-minute-increment minute options and an explicit
 * 12h/24h pill toggle so locale defaults are user-overridable per session.
 *
 * Internal value is always 24-hour `"HH:mm"`. The 12h/24h mode is a display
 * preference; it never modifies the value handed back via onChange.
 */
export function TimePicker({
	id,
	label,
	required,
	error,
	placeholder,
	value,
	onChange,
}: TimePickerProps) {
	const { t, locale } = useClientTranslations("track");
	const defaultIs12Hour = locale === "en" || locale === "es";

	const [is12Hour, setIs12Hour] = useState(defaultIs12Hour);
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);

	// Resolve the (hh, mm) pair driving the columns — falls back to next round half-hour.
	const parsed = parseHHMM(value);
	const fallback = useRef(nextRoundHalfHour()).current;
	const activeHh = parsed ? parsed.hh : fallback.hh;
	const activeMm = parsed ? snapMinuteToStep(parsed.mm) : fallback.mm;

	// When the locale changes mid-session, reset to the new locale default.
	useEffect(() => {
		setIs12Hour(defaultIs12Hour);
	}, [defaultIs12Hour]);

	const close = useCallback(() => setOpen(false), []);

	useEffect(() => {
		if (!open) return;
		const onPointer = (e: PointerEvent) => {
			if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
				close();
			}
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") close();
		};
		document.addEventListener("pointerdown", onPointer);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("pointerdown", onPointer);
			document.removeEventListener("keydown", onKey);
		};
	}, [open, close]);

	const emit = (hh: number, mm: number) => {
		onChange(formatHHMM(hh, mm));
	};

	const triggerLabelText = (() => {
		if (!parsed) {
			return placeholder ?? t("event.add.fields.time.placeholder");
		}
		if (!is12Hour) {
			return formatHHMM(parsed.hh, parsed.mm);
		}
		const { hour12, isPm } = to12(parsed.hh);
		const meridiemKey = isPm
			? "event.add.fields.time.pm"
			: "event.add.fields.time.am";
		return `${hour12}:${padTwo(parsed.mm)} ${t(meridiemKey)}`;
	})();

	const { hour12: activeHour12, isPm: activeIsPm } = to12(activeHh);

	return (
		<FormField id={id} label={label} required={required} error={error}>
			<div ref={rootRef} className={styles.root}>
				<button
					id={id}
					type="button"
					className={`${styles.trigger} ${parsed ? "" : styles.triggerEmpty}`}
					onClick={() => setOpen((v) => !v)}
					aria-expanded={open}
					aria-haspopup="dialog"
					aria-describedby={error ? `${id}-error` : undefined}
				>
					<ClockIcon className={styles.triggerIcon} aria-hidden="true" />
					<span className={styles.triggerLabel}>{triggerLabelText}</span>
					<ChevronDownIcon
						className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
						aria-hidden="true"
					/>
				</button>
				{open ? (
					<div className={styles.popover} role="dialog" aria-label={label}>
						<fieldset className={styles.modeToggle}>
							<legend className={styles.srOnly}>
								{t("event.add.fields.time.modeLegend")}
							</legend>
							<button
								type="button"
								className={styles.modeBtn}
								aria-pressed={is12Hour}
								onClick={() => setIs12Hour(true)}
							>
								{t("event.add.fields.time.mode12")}
							</button>
							<button
								type="button"
								className={styles.modeBtn}
								aria-pressed={!is12Hour}
								onClick={() => setIs12Hour(false)}
							>
								{t("event.add.fields.time.mode24")}
							</button>
						</fieldset>
						<div className={styles.columns}>
							<fieldset className={styles.column}>
								<legend className={styles.srOnly}>
									{t("event.add.fields.time.hoursColumn")}
								</legend>
								{is12Hour
									? HOURS_12.map((h12) => {
											const isSelected = h12 === activeHour12;
											return (
												<button
													key={`h12-${h12}`}
													type="button"
													className={`${styles.option} ${isSelected ? styles.optionSelected : ""}`}
													onClick={() =>
														emit(from12(h12, activeIsPm), activeMm)
													}
												>
													{h12}
												</button>
											);
										})
									: HOURS_24.map((h) => {
											const isSelected = h === activeHh;
											return (
												<button
													key={`h24-${h}`}
													type="button"
													className={`${styles.option} ${isSelected ? styles.optionSelected : ""}`}
													onClick={() => emit(h, activeMm)}
												>
													{padTwo(h)}
												</button>
											);
										})}
							</fieldset>
							<fieldset className={styles.column}>
								<legend className={styles.srOnly}>
									{t("event.add.fields.time.minutesColumn")}
								</legend>
								{MINUTE_OPTIONS.map((m) => {
									const isSelected = m === activeMm;
									return (
										<button
											key={`m-${m}`}
											type="button"
											className={`${styles.option} ${isSelected ? styles.optionSelected : ""}`}
											onClick={() => emit(activeHh, m)}
										>
											{padTwo(m)}
										</button>
									);
								})}
							</fieldset>
							{is12Hour ? (
								<fieldset className={styles.column}>
									<legend className={styles.srOnly}>
										{t("event.add.fields.time.meridiemColumn")}
									</legend>
									<button
										type="button"
										className={`${styles.option} ${activeIsPm ? "" : styles.optionSelected}`}
										onClick={() => emit(from12(activeHour12, false), activeMm)}
									>
										{t("event.add.fields.time.am")}
									</button>
									<button
										type="button"
										className={`${styles.option} ${activeIsPm ? styles.optionSelected : ""}`}
										onClick={() => emit(from12(activeHour12, true), activeMm)}
									>
										{t("event.add.fields.time.pm")}
									</button>
								</fieldset>
							) : null}
						</div>
					</div>
				) : null}
			</div>
		</FormField>
	);
}
